import * as React from "react";
import { Link } from "react-router-dom";
import {
  AlertCircle,
  Info,
  MoreHorizontal,
  RotateCcw,
  Search,
  SlidersHorizontal,
  X,
} from "lucide-react";
import {
  Button,
  Card,
  CardDescription,
  CardHeader,
  CardTitle,
  Checkbox,
  DataTable,
  DataTableEmpty,
  DataTableFooter,
  DataTablePagination,
  DataTableScroll,
  DataTableSelectionSummary,
  DataTableSkeleton,
  DataTableToolbar,
  DataTableToolbarSpacer,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Input,
  PageHeader,
  PageHeaderDescription,
  PageHeaderHeading,
  PageHeaderTitle,
  PageHeaderTop,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  cn,
  toast,
} from "@eduviaos/ui-kit";
import type { ApiError } from "@eduviaos/shared";
import { SeatPlanSheet } from "@/components/seating/SeatPlanSheet";
import { useCanEditSeating } from "@/hooks/useSeatingAuthoring";
import { usePageSizePreference } from "@/hooks/usePageSizePreference";
import { MIXED_DEFAULT_PLAN_LABELS } from "@/lib/seatAssignmentConfig/api";
import {
  SEAT_PLAN_KINDS,
  SEAT_PLAN_LABELS,
  planDetail,
  useBulkLearnerSeatPlans,
  useClearLearnerSeatPlan,
  useLearnerSeatPlans,
  usePutLearnerSeatPlan,
  type LearnerSeatPlanBody,
  type LearnerSeatPlanRow,
  type SeatPlanKind,
} from "@/lib/seating/plans";

/**
 * Phase SC-22c — learner seat plans (feature 4.68).
 *
 * Every learner on the roster with the plan that applies to them: their
 * own, or the centre default. Admins edit one learner from the row menu,
 * or select several and apply one plan to all of them. Plans only take
 * effect where seating is mixed (the whole centre, or a room set to
 * "Learner plans"), which the notice explains.
 */

const ANY_KIND = "__any__";

type SheetState =
  | { kind: "closed" }
  | { kind: "single"; row: LearnerSeatPlanRow }
  | { kind: "bulk"; rows: LearnerSeatPlanRow[] };

function formatDate(value: string | null): string {
  if (!value) return "—";
  const [y, m, d] = value.split("-").map(Number);
  if (!y || !m || !d) return value;
  return new Date(y, m - 1, d).toLocaleDateString("en-US", {
    month: "short",
    day: "2-digit",
    year: "numeric",
  });
}

export function SeatPlansPage() {
  const { canEditSeating, missingRbac } = useCanEditSeating();
  const [qInput, setQInput] = React.useState("");
  const [q, setQ] = React.useState("");
  const [kindFilter, setKindFilter] = React.useState<string>(ANY_KIND);
  const [page, setPage] = React.useState(0);
  const [pageSize, setPageSize] = usePageSizePreference({
    scope: "seating-plans",
    defaultPageSize: 10,
  });
  const [selected, setSelected] = React.useState<Set<string>>(() => new Set());
  const [sheet, setSheet] = React.useState<SheetState>({ kind: "closed" });

  React.useEffect(() => {
    const handle = window.setTimeout(() => {
      setQ(qInput);
      setPage(0);
    }, 250);
    return () => window.clearTimeout(handle);
  }, [qInput]);

  const query = useLearnerSeatPlans({
    q,
    kind: kindFilter === ANY_KIND ? undefined : (kindFilter as SeatPlanKind),
    limit: pageSize,
    offset: page * pageSize,
  });
  const { data, isLoading, isFetching, isError, refetch } = query;
  const rows = React.useMemo(() => data?.items ?? [], [data]);
  const total = data?.total ?? 0;

  const putPlan = usePutLearnerSeatPlan();
  const bulkPlan = useBulkLearnerSeatPlans();
  const clearPlan = useClearLearnerSeatPlan();

  const pageIds = rows.map((r) => r.student_id);
  const allOnPageSelected = pageIds.length > 0 && pageIds.every((id) => selected.has(id));
  const someOnPageSelected = pageIds.some((id) => selected.has(id));
  const selectedRows = rows.filter((r) => selected.has(r.student_id));

  function toggleAll(next: boolean) {
    setSelected((prev) => {
      const copy = new Set(prev);
      for (const id of pageIds) {
        if (next) copy.add(id);
        else copy.delete(id);
      }
      return copy;
    });
  }

  function toggleOne(id: string, next: boolean) {
    setSelected((prev) => {
      const copy = new Set(prev);
      if (next) copy.add(id);
      else copy.delete(id);
      return copy;
    });
  }

  function submit(body: LearnerSeatPlanBody) {
    if (sheet.kind === "single") {
      putPlan.mutate(
        { studentId: sheet.row.student_id, body },
        {
          onSuccess: () => {
            toast.success(`Saved ${sheet.row.learner_name}'s seat plan`);
            setSheet({ kind: "closed" });
          },
        },
      );
      return;
    }
    if (sheet.kind === "bulk") {
      bulkPlan.mutate(
        { studentIds: sheet.rows.map((r) => r.student_id), body },
        {
          onSuccess: (result) => {
            if (result.failed.length === 0) {
              toast.success(`Updated ${result.updated} seat plans`);
            } else {
              toast.warning(
                `Updated ${result.updated}; ${result.failed.length} couldn't be changed: ${result.failed[0]!.message}`,
              );
            }
            setSelected(new Set());
            setSheet({ kind: "closed" });
          },
        },
      );
    }
  }

  function reset(row: LearnerSeatPlanRow) {
    clearPlan.mutate(row.student_id, {
      onSuccess: () => toast.success(`${row.learner_name} now follows the centre default`),
      onError: (err: ApiError) => toast.error(err.message || "Couldn't reset the plan"),
    });
  }

  const mixedHere = data?.centre_mode === "mixed";
  const filtered = q !== "" || kindFilter !== ANY_KIND;
  const sheetError =
    sheet.kind === "single" ? putPlan.error : sheet.kind === "bulk" ? bulkPlan.error : null;

  return (
    <div className="space-y-6">
      <PageHeader>
        <PageHeaderTop>
          <PageHeaderTitle>
            <PageHeaderHeading>Seat plans</PageHeaderHeading>
            <PageHeaderDescription>
              How each learner uses seats: a permanent seat, fixed hours, flexible hours, a room, or
              no seat at all.
            </PageHeaderDescription>
          </PageHeaderTitle>
        </PageHeaderTop>
      </PageHeader>

      {missingRbac ? (
        <Card data-testid="seating-missing-rbac-notice" className="border-amber-300/60">
          <CardHeader>
            <CardTitle className="text-base">View-only access</CardTitle>
            <CardDescription>
              You don&apos;t have the <span className="font-medium">Manage zones</span> permission,
              so seat plans are read-only.
            </CardDescription>
          </CardHeader>
        </Card>
      ) : null}

      {data && !mixedHere ? (
        <Card data-testid="seat-plans-mode-notice" className="border-sky-300/60">
          <CardHeader>
            <div className="flex items-start gap-2">
              <Info aria-hidden="true" className="mt-0.5 size-4 shrink-0 text-sky-600" />
              <div className="space-y-1">
                <CardTitle className="text-base">Plans apply in mixed seating</CardTitle>
                <CardDescription>
                  Plans are used in rooms set to <span className="font-medium">Learner plans</span>,
                  or everywhere once the centre runs{" "}
                  <Link to="/seating/config" className="text-primary font-medium underline">
                    mixed seating
                  </Link>
                  .
                </CardDescription>
              </div>
            </div>
          </CardHeader>
        </Card>
      ) : null}

      {isError ? (
        <div
          role="alert"
          className="border-destructive/30 bg-destructive/5 text-destructive flex items-start gap-2 rounded-md border px-3 py-2 text-xs"
        >
          <AlertCircle aria-hidden="true" className="mt-0.5 size-3.5 shrink-0" />
          <span className="flex-1">Couldn&apos;t load seat plans.</span>
          <Button
            variant="ghost"
            size="sm"
            className="h-auto px-2 py-0.5 text-xs"
            onClick={() => void refetch()}
          >
            Retry
          </Button>
        </div>
      ) : null}

      <DataTable>
        <DataTableToolbar>
          <div className="relative w-full sm:w-64">
            <Search
              aria-hidden="true"
              className="text-muted-foreground absolute left-2.5 top-1/2 size-3.5 -translate-y-1/2"
            />
            <Input
              type="search"
              value={qInput}
              onChange={(e) => setQInput(e.target.value)}
              placeholder="Search learners…"
              className="pl-8"
              aria-label="Search learners"
            />
          </div>
          <Select
            value={kindFilter}
            onValueChange={(v) => {
              setKindFilter(v);
              setPage(0);
            }}
          >
            <SelectTrigger className="h-9 w-44" aria-label="Plan filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ANY_KIND}>Any plan</SelectItem>
              {SEAT_PLAN_KINDS.map((k) => (
                <SelectItem key={k} value={k}>
                  {SEAT_PLAN_LABELS[k]}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {filtered ? (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setQInput("");
                setKindFilter(ANY_KIND);
                setPage(0);
              }}
            >
              <X aria-hidden="true" className="size-3.5" />
              Clear
            </Button>
          ) : null}
          <DataTableToolbarSpacer />
          {data ? (
            <span className="text-muted-foreground text-xs">
              Default: {MIXED_DEFAULT_PLAN_LABELS[data.mixed_default_plan]}
            </span>
          ) : null}
          {canEditSeating && selectedRows.length > 0 ? (
            <Button
              size="sm"
              onClick={() => setSheet({ kind: "bulk", rows: selectedRows })}
              data-testid="seat-plans-bulk"
            >
              <SlidersHorizontal aria-hidden="true" className="size-4" />
              Set plan for {selectedRows.length}
            </Button>
          ) : null}
        </DataTableToolbar>

        <DataTableScroll>
          <Table>
            <TableCaption className="sr-only">Learner seat plans</TableCaption>
            <TableHeader>
              <TableRow>
                <TableHead className="w-10">
                  <span className="sr-only">Select row</span>
                  <Checkbox
                    aria-label="Select all"
                    checked={
                      allOnPageSelected ? true : someOnPageSelected ? "indeterminate" : false
                    }
                    onCheckedChange={(v) => toggleAll(v === true)}
                    disabled={!canEditSeating || rows.length === 0}
                  />
                </TableHead>
                <TableHead className="w-[30%]">Learner</TableHead>
                <TableHead className="w-[20%]">Plan</TableHead>
                <TableHead className="w-[30%]">Details</TableHead>
                <TableHead className="w-[14%]">Ends</TableHead>
                <TableHead className="w-[1%] text-right">
                  <span className="sr-only">Actions</span>
                </TableHead>
              </TableRow>
            </TableHeader>
            {isLoading ? (
              <DataTableSkeleton rows={5} columns={6} />
            ) : (
              <TableBody className={cn(isFetching && "opacity-80")}>
                {rows.map((row) => {
                  const isDefault = row.source === "mixed_default";
                  return (
                    <TableRow key={row.student_id} data-testid={`seat-plan-row-${row.student_id}`}>
                      <TableCell>
                        <Checkbox
                          aria-label={`Select ${row.learner_name}`}
                          checked={selected.has(row.student_id)}
                          onCheckedChange={(v) => toggleOne(row.student_id, v === true)}
                          disabled={!canEditSeating}
                        />
                      </TableCell>
                      <TableCell className="font-medium">{row.learner_name}</TableCell>
                      <TableCell>
                        <span className={cn(isDefault && "text-muted-foreground")}>
                          {SEAT_PLAN_LABELS[row.kind]}
                        </span>
                        {isDefault ? (
                          <span className="text-muted-foreground/80 ml-1.5 text-xs">(default)</span>
                        ) : null}
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {planDetail(row) || "—"}
                      </TableCell>
                      <TableCell className="text-muted-foreground tabular-nums">
                        <span title={row.valid_until ?? undefined}>
                          {formatDate(row.valid_until)}
                        </span>
                      </TableCell>
                      <TableCell className="text-right">
                        {canEditSeating ? (
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="size-7"
                                aria-label={`Actions for ${row.learner_name}`}
                                data-testid={`seat-plan-actions-${row.student_id}`}
                              >
                                <MoreHorizontal aria-hidden="true" className="size-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="w-48">
                              <DropdownMenuItem
                                onSelect={() => setSheet({ kind: "single", row })}
                                data-testid={`seat-plan-edit-${row.student_id}`}
                              >
                                <SlidersHorizontal aria-hidden="true" className="size-4" />
                                Edit plan
                              </DropdownMenuItem>
                              {!isDefault ? (
                                <DropdownMenuItem onSelect={() => reset(row)}>
                                  <RotateCcw aria-hidden="true" className="size-4" />
                                  Use centre default
                                </DropdownMenuItem>
                              ) : null}
                            </DropdownMenuContent>
                          </DropdownMenu>
                        ) : (
                          <span className="text-muted-foreground text-xs">—</span>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            )}
          </Table>
        </DataTableScroll>

        {!isLoading && rows.length === 0 ? (
          <DataTableEmpty
            icon={<SlidersHorizontal aria-hidden="true" />}
            title={filtered ? "No matches" : "No learners yet"}
            description={
              filtered
                ? "Adjust the search or the plan filter."
                : "Learners appear here once they are on your roster."
            }
          />
        ) : null}

        <DataTableFooter>
          <DataTableSelectionSummary selected={selected.size} total={total} />
          <DataTablePagination
            className="ml-auto"
            page={page + 1}
            pageSize={pageSize}
            totalRows={total}
            isFetching={isFetching}
            onPageChange={(next) => setPage(Math.max(0, next - 1))}
            onPageSizeChange={(next) => {
              setPageSize(next);
              setPage(0);
            }}
            testIdPrefix="seat-plans-pagination"
          />
        </DataTableFooter>
      </DataTable>

      <SeatPlanSheet
        open={sheet.kind !== "closed"}
        onOpenChange={(open) => {
          if (!open) {
            setSheet({ kind: "closed" });
            putPlan.reset();
            bulkPlan.reset();
          }
        }}
        learnerNames={
          sheet.kind === "single"
            ? [sheet.row.learner_name]
            : sheet.kind === "bulk"
              ? sheet.rows.map((r) => r.learner_name)
              : []
        }
        current={sheet.kind === "single" ? sheet.row : null}
        pending={putPlan.isPending || bulkPlan.isPending}
        error={sheetError}
        onSubmit={submit}
      />
    </div>
  );
}
